import { useState } from 'react';
import {
    Box,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    IconButton,
    Skeleton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RotateRightIcon from '@mui/icons-material/RotateRight';

interface MediaPreviewCardProps {
    url: string;
    filename: string;
    fileType?: string;
    onDelete?: () => void;
    onRotate?: () => void;
    showFooter?: boolean;
    height?: string | number;
    minHeight?: string | number;
    aspectRatio?: string;
    enableRotation: boolean;
    enableDelete: boolean;
}

export default function MediaPreviewCard({
    url,
    filename,
    fileType,
    onDelete,
    onRotate,
    enableRotation = true,
    enableDelete = true,
    showFooter = true,
    height = '100%',
    minHeight = '200px',
    aspectRatio = 'auto',
}: MediaPreviewCardProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const isVideo = fileType?.startsWith('video');

    const handleMediaLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const handleMediaError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    const footerHeight = showFooter ? '60px' : '0px';
    const mediaContainerHeight = showFooter ? `calc(100% - ${footerHeight})` : '100%';

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                height: height,
                minHeight: minHeight,
                border: '1px solid #ccc',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    height: mediaContainerHeight,
                    position: 'relative',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden',
                }}
            >
                {isLoading && (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height="100%"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 1,
                        }}
                    />
                )}
                
                {hasError ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: 'text.secondary',
                        }}
                    >
                        <Typography variant="body2">Failed to load media</Typography>
                        <Typography variant="caption">{filename}</Typography>
                    </Box>
                ) : (
                    <CardMedia
                        component={isVideo ? 'video' : 'img'}
                        src={url}
                        width='100%'
                        height='100%'
                        controls={isVideo}
                        alt={filename}
                        onLoad={handleMediaLoad}
                        onError={handleMediaError}
                        onLoadedData={isVideo ? handleMediaLoad : undefined}
                        sx={{
                            objectFit: 'contain',
                            aspectRatio:{aspectRatio},
                            display: isLoading ? 'none' : 'block',
                        }}
                    />
                )}
            </Box>

            {showFooter && (
                <CardContent
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        px: 2,
                        height: footerHeight,
                        minHeight: footerHeight,
                        '&:last-child': { pb: 1 },
                        borderTop: '1px solid #eee',
                    }}
                >
                    <Box sx={{ overflow: 'hidden', pr: 1, flex: 1 }}>
                        <Typography
                            variant="subtitle2"
                            component="div"
                            noWrap
                            title={filename}
                        >
                            {filename}
                        </Typography>
                    </Box>
                    <CardActions sx={{ p: 0 }}>
                        {!isVideo && onRotate && enableRotation && (
                            <IconButton
                                aria-label="rotate media"
                                onClick={onRotate}
                                size="small"
                            >
                                <RotateRightIcon />
                            </IconButton>
                        )}
                        {onDelete && enableDelete && (
                            <IconButton
                                aria-label="delete media"
                                onClick={onDelete}
                                color="error"
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </CardActions>
                </CardContent>
            )}
        </Card>
    );
}